import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { connect } from "react-redux";
import { setNewPasswordRecovery, setSuccessFalse } from "../../store/actions/index.js";
import { fireAnalytics } from "../../utils/firebase.js";
import { ArrowLeft, Eye, EyeOff, Info } from "lucide-react";
import styles from "./RecuperarPassword.module.scss";
import LogoEdify from "../../assets/images/Logo-edify-no-bg.png";

interface FormData {
  password: string;
  repeatPassword: string;
}

interface FormErrors {
  password?: string;
  repeatPassword?: string;
}

interface RecuperarPasswordProps {
  success: boolean;
  setNewPasswordRecovery: (value: {
    values: FormData;
    token: string;
    navigate: ReturnType<typeof useNavigate>;
  }) => void;
  setSuccessFalse: () => void;
}

const RecuperarPassword: React.FC<RecuperarPasswordProps> = ({
  success,
  setNewPasswordRecovery,
  setSuccessFalse,
}) => {
  const [formData, setFormData] = useState<FormData>({
    password: "",
    repeatPassword: "",
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showRepeatPassword, setShowRepeatPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useParams<{ token: string }>();
  const sourceParam = (new URLSearchParams(location.search).get("source") ?? "").trim().toLowerCase();
  const isJobboardLegacySource = sourceParam === "jobboard-legacy";

  useEffect(() => {
    setSuccessFalse();
  }, [setSuccessFalse]);

  const validateForm = () => {
    const nextErrors: FormErrors = {};
    if (!formData.password) {
      nextErrors.password = "El campo contraseña es requerido";
    } else if (formData.password.length < 6) {
      nextErrors.password = "Debe contener al menos 6 caracteres.";
    }

    if (!formData.repeatPassword) {
      nextErrors.repeatPassword = "Por favor confirma la contraseña.";
    } else if (formData.repeatPassword !== formData.password) {
      nextErrors.repeatPassword = "La contraseña no coincide.";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validateForm() || !token) return;
    setIsSubmitting(true);
    setNewPasswordRecovery({ values: formData, token, navigate });
    fireAnalytics("change_password", { category: "authentication" });
    setTimeout(() => setIsSubmitting(false), 600);
  };

  const handleBackToLogin = () => {
    setSuccessFalse();
    navigate(isJobboardLegacySource ? "/bolsadetrabajo" : "/");
  };

  if (success) {
    return (
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.successContainer}>
            <motion.div
              className={styles.successIcon}
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5, type: "spring" }}
            >
              ✓
            </motion.div>
            <motion.h1
              className={styles.successTitle}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              ¡Listo!
            </motion.h1>
            <motion.p
              className={styles.successMessage}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              {isJobboardLegacySource
                ? "Tu contraseña se cambió correctamente. Ya podés continuar en la bolsa de trabajo."
                : "Tu contraseña se cambió correctamente. Ya podés iniciar sesión."}
            </motion.p>
            <motion.button
              className={styles.backButton}
              onClick={handleBackToLogin}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              {isJobboardLegacySource ? "Ir a bolsa de trabajo" : "Ir al login"}
            </motion.button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.header}>
          <motion.img
            src={LogoEdify}
            alt="Edify Logo"
            className={styles.logo}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          />
          <motion.h1
            className={styles.mainTitle}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Creá tu nueva contraseña
          </motion.h1>
          <motion.p
            className={styles.mainSubtitle}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            Ingresá una contraseña nueva para recuperar el acceso a tu cuenta.
          </motion.p>
        </div>

        <div className={styles.formContainer}>
          <motion.form
            className={`${styles.form} ${styles.animationContainer}`}
            onSubmit={handleSubmit}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <ul className={styles.helperList}>
              <li className={styles.helperItem}>
                <Info size={14} />
                Debe contener al menos 6 caracteres.
              </li>
              <li className={styles.helperItem}>
                <Info size={14} />
                Deben coincidir ambas contraseñas.
              </li>
            </ul>

            <div className={styles.inputGroup}>
              <label className={styles.label} htmlFor="password">
                Nueva contraseña
              </label>
              <div className={styles.passwordGroup}>
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Ingresá tu nueva contraseña"
                  className={`${styles.input} ${errors.password ? styles.inputError : ""}`}
                  value={formData.password}
                  onChange={(e) => handleInputChange("password", e.target.value)}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className={styles.eyeButton}
                  onClick={() => setShowPassword((prev) => !prev)}
                  aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {errors.password && <span className={styles.errorText}>{errors.password}</span>}
            </div>

            <div className={styles.inputGroup}>
              <label className={styles.label} htmlFor="repeatPassword">
                Repetir contraseña
              </label>
              <div className={styles.passwordGroup}>
                <input
                  id="repeatPassword"
                  type={showRepeatPassword ? "text" : "password"}
                  placeholder="Repetí tu nueva contraseña"
                  className={`${styles.input} ${errors.repeatPassword ? styles.inputError : ""}`}
                  value={formData.repeatPassword}
                  onChange={(e) => handleInputChange("repeatPassword", e.target.value)}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className={styles.eyeButton}
                  onClick={() => setShowRepeatPassword((prev) => !prev)}
                  aria-label={showRepeatPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                >
                  {showRepeatPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {errors.repeatPassword && <span className={styles.errorText}>{errors.repeatPassword}</span>}
            </div>

            <button
              type="submit"
              disabled={isSubmitting || !formData.password || !formData.repeatPassword}
              className={`${styles.submitButton} ${isSubmitting ? styles.loading : ""}`}
            >
              {isSubmitting ? "Guardando..." : "Cambiar contraseña"}
            </button>
          </motion.form>

          <motion.div
            className={styles.goBack}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.8 }}
          >
            <Link to={isJobboardLegacySource ? "/bolsadetrabajo" : "/"} className={styles.goBackLink}>
              <ArrowLeft size={16} />
              <span>{isJobboardLegacySource ? "Ir a bolsa de trabajo" : "Volver al login"}</span>
            </Link>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state: { general: { success: boolean } }) => ({
  success: state.general.success,
});

export default connect(mapStateToProps, {
  setNewPasswordRecovery,
  setSuccessFalse,
})(RecuperarPassword);
